import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import { getHasPremiumArticleEloquaForm } from '@/store/articles/selectors';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from 'next-localization';
import useSidebar from 'hooks/useSidebar';
import styles from './ArticleChapters.module.scss';
import AnimateHeight, { Height } from 'react-animate-height';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

const HEADER_HEIGHT_DEFAULT_VALUE = 96;

type Props = ComponentProps & {
  fields: {
    items: {
      displayName: string;
      id: string;
      name: string;
      url: string;
      fields: {
        anchor: {
          value: string;
        };
        title: {
          value: string;
        };
      };
    }[];
  };
};

const ArticleChapters = ({ fields, rendering }: Props) => {
  const { sitecoreContext: context } = useSitecoreContext();
  const havePremiumArticleEloquaForm = useSelector(getHasPremiumArticleEloquaForm);
  const { t } = useI18n();
  const isExperienceEditor = context.pageEditing;
  const chaptersWrapper = useRef(null);
  const chaptersDropdown = useRef(null);

  const firstId: string = fields.items[0].fields.anchor.value;
  const [height, setHeight] = useState<Height>(0);

  const {
    setShowScroll,
    activeSubtitle,
    setActiveSubtitle,
    showDropdown,
    dropdownHeight,
    sidebarWidth,
    setDropdownHeightValue,
    setSidebarWidthValue,
  } = useSidebar({
    dropdownRef: chaptersDropdown,
    wrapperRef: chaptersWrapper,
    firstId,
    items: fields.items,
  });

  const chapters = useMemo(() => {
    return fields.items || [];
  }, [fields.items]);

  const computeHeight =
    (typeof window !== 'undefined' && document.getElementById('headerContent')?.offsetHeight) ||
    HEADER_HEIGHT_DEFAULT_VALUE;

  const getActiveSubtitleName = useMemo(() => {
    const currentTitle = chapters.filter((item) => item.fields.anchor.value === activeSubtitle)[0]
      ?.fields.title.value;
    return currentTitle;
  }, [chapters, activeSubtitle]);

  const getScrollToSectionId = (title) => {
    const id: string = havePremiumArticleEloquaForm
      ? 'premium-eloqua-form'
      : title.fields.anchor.value;
    document.getElementById(id).scrollIntoView();
    setActiveSubtitle(id);
  };

  useEffect(() => {
    if (chapters.length) {
      setActiveSubtitle(fields.items[0].fields.anchor.value);
      setSidebarWidthValue(chaptersWrapper);
      setDropdownHeightValue();
      window.addEventListener('resize', setSidebarWidthValue(chaptersWrapper));
    }

    return () => {
      window.removeEventListener('resize', setSidebarWidthValue);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    (chapters.length || isExperienceEditor) && (
      <div>
        <div data-e2e-component-uid={rendering.uid} className="w-100 realtive">
          <div
            ref={chaptersWrapper}
            className={`${styles['chapters-wrapper']} chapters-wrapper mb-6 mb-xl-0`}
          >
            <nav style={{ width: `${sidebarWidth}px` }}>
              <h3 className="mb-5">{t('articleChaptersTitle')}</h3>
              <div className="flex flex-column w-100">
                {fields.items.map((title, i) => (
                  <div key={`${title.fields.anchor.value}-${i}`}>
                    {title.fields.title && title.fields.title.value && (
                      <p
                        className={`${styles.title} text-uppercase py-1 ps-3 ${
                          activeSubtitle === title.fields.anchor.value ? styles.active : ''
                        }`}
                        tabIndex="0"
                        onClick={() => getScrollToSectionId(title)}
                        onKeyDown={(e) => {
                          if (e.code === 'Enter') {
                            getScrollToSectionId(title);
                          }
                        }}
                      >
                        {title.fields.title.value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </nav>
          </div>
          {showDropdown && (
            <div
              className={`${styles['chapters-dropdown']} flex-column`}
              style={{ top: `${computeHeight}px` }}
            >
              <button
                ref={chaptersDropdown}
                aria-expanded="false"
                className={`${styles['chapters-dropdown-btn']} dropdown-button collapsed pe-2`}
                type="button"
                onClick={() => {
                  setShowScroll((prev) => !prev);
                  setHeight(height === 0 ? 'auto' : 0);
                }}
              >
                <div className="d-flex align-items-center container expandable">
                  <p className="text-left text-uppercase dropdown-spacing text-black">
                    {getActiveSubtitleName}
                  </p>
                  <em
                    className={`d-flex bi-chevron-${height ? 'up' : 'down'} ms-auto ms-sm-3`}
                    aria-hidden="true"
                  />
                </div>
              </button>
              <AnimateHeight height={height} duration={200}>
                <div
                  id="chapters-dropdown-btn__id"
                  className={`collapse background-white ${styles['chapters-dropdown-btn__id']}`}
                >
                  <div
                    className="copywrite-dropdown pb-1"
                    style={{ maxHeight: `calc(100vh - ${computeHeight + dropdownHeight}px)` }}
                  >
                    {fields.items.map(
                      (title, i) =>
                        title.fields.title &&
                        title.fields.title.value && (
                          <div
                            key={`${title.fields.anchor.value}-${i}`}
                            className={`${styles.title} ${styles['dropdown-title']} ${
                              styles['dropdown-spacing']
                            } text-uppercase py-2 ${
                              activeSubtitle === title.fields.anchor.value ? styles.active : ''
                            }`}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.code === 'Enter') {
                                getScrollToSectionId(title);
                              }
                            }}
                            onClick={() => {
                              getScrollToSectionId(title);
                              chaptersDropdown.current.click();
                            }}
                          >
                            <div className="container">{title.fields.title.value}</div>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </AnimateHeight>
            </div>
          )}
        </div>
      </div>
    )
  );
};

export default withDatasourceCheck()(ArticleChapters);
